// Recebimentos.js - Painel de Tesouraria
// ==============================================
// 🔥 v21 — RECIBOS SÓ APARECEM NO TIPO "ENTRADA"
//    ✅ Secção "Recibos a Agrupar" oculta em Sangria e Reforço
//    ✅ Limpeza automática de recibos ao mudar de tipo
//    ✅ handleCreate só envia recibos quando tipo=Entrada
// 🔥 v20 — PAGINAÇÃO NOS DROPDOWNS (compras e recibos)
//    ✅ 8 itens por página em cada dropdown
//    ✅ Paginação estilo "1, 2, 3, ..., 5, 6, 7"
//    ✅ Reset automático da página ao pesquisar
//    ✅ Compras já ligadas a recebimentos ativos não aparecem no dropdown
// 🔥 v19 — LISTA DE PRODUTOS NAS COMPRAS AGRUPADAS (modal detalhes)
// 🔥 v18 — COMPRAS AGRUPADAS NO MODAL DE DETALHES / PDF
// 🔥 v17 — CAMPO "VALOR RECEBIDO" SOMA OS PREÇOS TOTAIS
// 🔥 v16 — FOOTER SOMA OS PREÇOS TOTAIS (verde) E CAMPO VALOR SOMA O SALDO
// 🔥 v15 — DROPDOWN DE COMPRAS COM CHECKBOX PERMANENTE
// 🔥 v14 — FIX CRÍTICO: DROPDOWN DAS COMPRAS MANTÉM-SE ABERTO
// 🔥 v13 — FLUXO DE COMPRAS IDÊNTICO AOS RECIBOS (mantido)
// 🔥 v12 — DEBUG (removido)
// 🔥 v11 — CACHE HTTP (mantido)
// 🔥 v10 — AGRUPAMENTO POR NF NORMALIZADA (mantido)
// ==============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import './Recebimentos.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

// =============================================
// 🔥 v20 — CONSTANTE DE PAGINAÇÃO DO DROPDOWN
// =============================================
const ITENS_POR_PAGINA_DROPDOWN = 8;

// =============================================
// CONSTANTES
// =============================================
const TIPOS_MOVIMENTO = [
    { value: 'Entrada', label: 'Entrada', icon: 'arrow_downward', color: '#059669', classe: 'rb-badge-entrada' },
    { value: 'Sangria', label: 'Sangria', icon: 'arrow_upward', color: '#dc2626', classe: 'rb-badge-sangria' },
    { value: 'Reforço', label: 'Reforço', icon: 'add_circle', color: '#4f46e5', classe: 'rb-badge-reforco' }
];

const STATUS_RECEBIMENTO = [
    { value: 'Ativo', label: 'Ativo', color: '#10b981', icon: 'check_circle' },
    { value: 'Anulado', label: 'Anulado', color: '#dc2626', icon: 'cancel' }
];

const METODOS_PAGAMENTO = [
    { value: 'Dinheiro', label: 'Dinheiro' },
    { value: 'Transferência Bancária', label: 'Transferência' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Cartão', label: 'Cartão' },
    { value: 'M-kesh', label: 'M-kesh' },
    { value: 'M-Pesa', label: 'M-Pesa' },
    { value: 'E-Mola', label: 'E-Mola' },
    { value: 'Outro', label: 'Outro' }
];

const TIPOS_CONTA = [
    { value: 'Caixa', label: 'Caixa', icon: 'payments' },
    { value: 'Banco', label: 'Banco', icon: 'account_balance' },
    { value: 'Mobile', label: 'Mobile', icon: 'phone_android' }
];

// =============================================
// FALLBACK DE MOEDA
// =============================================
const MOEDA_FALLBACK = {
    simbolo: 'MT',
    codigo: 'MZN',
    casasDecimais: 2,
    separadorDecimal: ',',
    separadorMilhar: '.',
    posicaoSimbolo: 'depois'
};

const FORMATACAO_FALLBACK = {
    data: { formato: 'DD/MM/YYYY', separador: '/' },
    numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
};

// =============================================
// 🔥 HELPER: extrair valor numérico robusto
// =============================================
function extrairValorNumerico(valor) {
    if (valor === undefined || valor === null) return 0;
    if (typeof valor === 'number') return isNaN(valor) ? 0 : valor;

    if (typeof valor === 'object' && valor.$numberDecimal !== undefined) {
        const n = parseFloat(valor.$numberDecimal);
        return isNaN(n) ? 0 : n;
    }

    if (typeof valor === 'object') {
        if (typeof valor.toString === 'function') {
            const s = valor.toString();
            if (s && s !== '[object Object]') {
                const n = parseFloat(s.replace(',', '.'));
                if (!isNaN(n)) return n;
            }
        }
        return 0;
    }

    const str = String(valor).trim();
    if (!str || str === 'undefined' || str === 'null') return 0;

    if (str.includes(',') && str.includes('.')) {
        const limpo = str
            .replace(/[^\d.,-]/g, '')
            .replace(/\./g, '')
            .replace(',', '.');
        const n = parseFloat(limpo);
        return isNaN(n) ? 0 : n;
    }

    if (str.includes(',')) {
        const limpo = str.replace(/[^\d,-]/g, '').replace(',', '.');
        const n = parseFloat(limpo);
        return isNaN(n) ? 0 : n;
    }

    const limpo = str.replace(/[^\d.-]/g, '');
    const n = parseFloat(limpo);
    return isNaN(n) ? 0 : n;
}

// =============================================
// 🔥 HELPER: normalizar NF para comparação robusta
// =============================================
function normalizarNF(valor) {
    if (!valor) return '';
    return String(valor)
        .trim()
        .toUpperCase()
        .replace(/[\s\-_\/\\.]/g, '');
}

// =============================================
// 🔥 v19 — HELPER: extrair itens/produtos de uma compra
// =============================================
function extrairItensCompra(compra) {
    if (!compra) return [];

    const arrayCandidatos = [
        compra.itens,
        compra.produtos,
        compra.linhas,
        compra.items,
        compra.produtosCompra,
        compra.listaProdutos
    ];

    const lista = arrayCandidatos.find(arr => Array.isArray(arr) && arr.length > 0);
    if (!lista) return [];

    return lista.map(item => {
        if (!item || typeof item !== 'object') return null;

        const produtoNome =
            item.produtoNome ||
            item.nomeProduto ||
            item.descricaoProduto ||
            item.descricao ||
            item.nome ||
            item.produto?.nome ||
            item.produto?.descricao ||
            'Produto';

        const quantidade = extrairValorNumerico(
            item.quantidade ?? item.qtd ?? item.quantity ?? item.qty ?? 0
        );

        const precoUnitario = extrairValorNumerico(
            item.precoUnitario ?? item.preco ?? item.valorUnitario ?? item.precoVenda ?? item.valor ?? 0
        );

        const subtotal = extrairValorNumerico(
            item.subtotal ?? item.total ?? (quantidade * precoUnitario)
        );

        const unidade = item.unidade || item.unid || item.medida || '';

        return {
            produtoNome: String(produtoNome).trim(),
            quantidade,
            precoUnitario,
            subtotal,
            unidade
        };
    }).filter(Boolean);
}

// =============================================
// HELPER: Gerar timestamp legível
// =============================================
function gerarTimestampFicheiro() {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');
    const hora = String(agora.getHours()).padStart(2, '0');
    const minuto = String(agora.getMinutes()).padStart(2, '0');
    const segundo = String(agora.getSeconds()).padStart(2, '0');

    return `${ano}-${mes}-${dia}_${hora}-${minuto}-${segundo}`;
}

// =============================================
// 🔥 v20 — HELPER: gerar números de página estilo "1,2,3,...,5,6,7"
// =============================================
function gerarNumerosPagina(atual, total) {
    const arr = [];

    if (total <= 5) {
        for (let i = 1; i <= total; i++) arr.push(i);
        return arr;
    }

    arr.push(1);

    let ini = Math.max(2, atual - 1);
    let fim = Math.min(total - 1, atual + 1);

    if (atual <= 3) {
        ini = 2;
        fim = Math.min(total - 1, 4);
    }
    if (atual >= total - 2) {
        ini = Math.max(2, total - 3);
        fim = total - 1;
    }

    if (ini > 2) arr.push('...');
    for (let i = ini; i <= fim; i++) arr.push(i);
    if (fim < total - 1) arr.push('...');

    arr.push(total);
    return arr;
}

// =============================================
// TOAST
// =============================================
function Toast({ message, type, onClose }) {
    useEffect(() => {
        if (message) {
            const t = setTimeout(onClose, 4000);
            return () => clearTimeout(t);
        }
    }, [message, onClose]);

    if (!message) return null;

    const icons = { success: 'check_circle', danger: 'error', warning: 'warning', info: 'info' };
    const colors = { success: '#10b981', danger: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };

    return (
        <div className="rb-toast-container">
            <div className={`rb-toast-message rb-toast-${type}`}
                style={{ borderLeftColor: colors[type] }}>
                <Icon name={icons[type] || 'info'} size={20}
                    style={{ color: colors[type] }} />
                <span className="rb-toast-text">{message}</span>
                <button onClick={onClose} className="rb-toast-close-btn">
                    <Icon name="close" size={16} />
                </button>
            </div>
        </div>
    );
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function Recebimentos() {
    // =============================================
    // ESTADOS
    // =============================================
    const [recebimentos, setRecebimentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('todos');
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [filtroMetodo, setFiltroMetodo] = useState('todos');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');

    const [toast, setToast] = useState({ message: '', type: '' });
    const showToast = (text, type = 'success') => setToast({ message: text, type });
    const closeToast = () => setToast({ message: '', type: '' });

    const [empresaConfig, setEmpresaConfig] = useState({
        moeda: MOEDA_FALLBACK,
        formatacao: FORMATACAO_FALLBACK
    });

    const [empresaInfo, setEmpresaInfo] = useState(null);

    const [saldos, setSaldos] = useState({ contas: [], totalGeral: null });
    const [resumoDia, setResumoDia] = useState(null);
    const [contasDisponiveis, setContasDisponiveis] = useState({
        caixas: [], bancos: [], operadoras: [], todos: []
    });

    const [pagination, setPagination] = useState({
        page: 1, limit: 10, total: 0, pages: 0
    });

    // Modais
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAnularModal, setShowAnularModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Modal de escolha de método
    const [showMetodoModal, setShowMetodoModal] = useState(false);
    const [metodosDetectados, setMetodosDetectados] = useState([]);

    const [recebimentoDetalhes, setRecebimentoDetalhes] = useState(null);
    const [recebimentoToAnular, setRecebimentoToAnular] = useState(null);
    const [recebimentoToDelete, setRecebimentoToDelete] = useState(null);
    const [motivoAnulacao, setMotivoAnulacao] = useState('');

    // Compras a fornecedor (múltiplas)
    const [comprasDisponiveis, setComprasDisponiveis] = useState([]);
    const [comprasSelecionadas, setComprasSelecionadas] = useState([]);
    const [loadingCompras, setLoadingCompras] = useState(false);
    const [searchCompra, setSearchCompra] = useState('');
    const [showComprasDropdown, setShowComprasDropdown] = useState(false);

    // =============================================
    // 🔥 v20 — PAGINAÇÃO DOS DROPDOWNS
    // =============================================
    const [paginaCompras, setPaginaCompras] = useState(1);
    const [paginaRecibos, setPaginaRecibos] = useState(1);

    // =============================================
    // FORMULÁRIO
    // =============================================
    const [formData, setFormData] = useState({
        tipo: 'Entrada',
        valor: 0,
        metodoPagamento: 'Dinheiro',
        referencia: '',
        data: new Date().toISOString().split('T')[0],
        observacoes: '',
        contaTipo: 'Caixa',
        contaCaixaNome: 'Caixa Principal',
        contaBancoId: '',
        contaOperadora: 'M-Pesa',
        contaNumeroTelefone: '',
        compraFornecedorId: null
    });

    // =============================================
    // RECIBOS ELEGÍVEIS + DROPDOWN
    // =============================================
    const [recibosElegiveis, setRecibosElegiveis] = useState([]);
    const [recibosSelecionados, setRecibosSelecionados] = useState([]);
    const [searchRecibo, setSearchRecibo] = useState('');
    const [loadingRecibos, setLoadingRecibos] = useState(false);
    const [showRecibosDropdown, setShowRecibosDropdown] = useState(false);

    // Refs
    const bodyModalRef = useRef(null);
    const dropdownRecibosRef = useRef(null);
    const dropdownComprasRef = useRef(null);
    const searchReciboTimeoutRef = useRef(null);

    // =============================================
    // API INSTANCE COM ANTI-CACHE
    // =============================================
    const api = useMemo(() => axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
        }
    }), []);

    // =============================================
    // APLICAR CONFIGURAÇÃO DA EMPRESA
    // =============================================
    const aplicarConfiguracao = useCallback((configuracao, empresa) => {
        if (configuracao) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: configuracao.moeda || prev.moeda || MOEDA_FALLBACK,
                formatacao: configuracao.formatacao || prev.formatacao || FORMATACAO_FALLBACK
            }));
        }
        if (empresa) {
            setEmpresaInfo(empresa);
        }
    }, []);

    // =============================================
    // MOEDA SEGURA
    // =============================================
    const moedaSegura = useMemo(() => {
        const m = empresaConfig?.moeda || {};
        return {
            simbolo: m.simbolo || 'MT',
            codigo: m.codigo || 'MZN',
            casasDecimais: Number.isInteger(m.casasDecimais) ? m.casasDecimais : 2,
            separadorDecimal: m.separadorDecimal || ',',
            separadorMilhar: m.separadorMilhar || '.'
        };
    }, [empresaConfig]);

    // =============================================
    // FORMATAR MOEDA
    // =============================================
    const formatarMoeda = useCallback((valor) => {
        const cfg = moedaSegura;
        const numero = extrairValorNumerico(valor);

        if (isNaN(numero) || numero === null || numero === undefined) {
            return `0${cfg.separadorDecimal}00 ${cfg.simbolo}`;
        }

        let formatado = numero.toFixed(cfg.casasDecimais);
        formatado = formatado.replace('.', cfg.separadorDecimal);
        const partes = formatado.split(cfg.separadorDecimal);
        partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, cfg.separadorMilhar);
        formatado = partes.join(cfg.separadorDecimal);

        return `${formatado} ${cfg.simbolo}`;
    }, [moedaSegura]);

    // =============================================
    // FORMATAR QUANTIDADE
    // =============================================
    const formatarQuantidade = useCallback((valor) => {
        const n = extrairValorNumerico(valor);
        if (Number.isInteger(n)) return String(n);
        return n.toFixed(2).replace('.', ',');
    }, []);

    // =============================================
    // FORMATAR DATA
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig?.formatacao?.data || FORMATACAO_FALLBACK.data;
        const formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();

        return formato
            .replace('DD', dia)
            .replace('MM', mes)
            .replace('YYYY', ano)
            .replace('YY', String(ano).slice(-2));
    }, [empresaConfig]);

    // =============================================
    // HELPERS
    // =============================================
    const getTipoInfo = (tipo) => TIPOS_MOVIMENTO.find(t => t.value === tipo) || TIPOS_MOVIMENTO[0];
    const getStatusInfo = (status) => STATUS_RECEBIMENTO.find(s => s.value === status) || STATUS_RECEBIMENTO[0];

    const getContaLabel = (contaDestino) => {
        if (!contaDestino) return '-';
        if (contaDestino.tipo === 'Banco') {
            return [contaDestino.bancoNome, contaDestino.conta].filter(Boolean).join(' — ') || 'Banco';
        }
        if (contaDestino.tipo === 'Mobile') {
            return [contaDestino.operadora, contaDestino.numeroTelefone].filter(Boolean).join(' — ') || 'Mobile';
        }
        return contaDestino.caixaNome || 'Caixa';
    };

    const getContaIcon = (tipo) => {
        if (tipo === 'Banco') return 'account_balance';
        if (tipo === 'Mobile') return 'phone_android';
        return 'payments';
    };

    const getLinhaClasse = (tipo, status) => {
        if (status === 'Anulado') return 'rb-row-cancelled';
        if (tipo === 'Entrada') return 'rb-row-entrada';
        if (tipo === 'Sangria') return 'rb-row-sangria';
        if (tipo === 'Reforço') return 'rb-row-reforco';
        return '';
    };

    // =============================================
    // HELPERS DE AGRUPAMENTO DE RECIBOS (por fatura)
    // =============================================
    const getChaveAgrupamento = (recibo) => {
        if (recibo.faturaSnapshot?._id) return `fat_${recibo.faturaSnapshot._id}`;
        if (recibo.faturaId) return `fat_${recibo.faturaId}`;
        if (recibo.faturaSnapshot?.numero) return `num_${recibo.faturaSnapshot.numero}`;
        return `rec_${recibo._id}`;
    };

    const podeSelecionarRecibo = (recibo) => {
        if (recibosSelecionados.length === 0) return true;
        const chavePrimeiro = getChaveAgrupamento(recibosSelecionados[0]);
        const chaveAtual = getChaveAgrupamento(recibo);
        return chavePrimeiro === chaveAtual;
    };

    const isReciboSelecionado = (recibo) =>
        recibosSelecionados.some(r => r._id === recibo._id);

    // =============================================
    // HELPERS DE AGRUPAMENTO DE COMPRAS
    // =============================================
    const getChaveAgrupamentoCompra = (compra) => {
        if (!compra) return null;
        const nf = normalizarNF(compra.documentoFiscal);
        if (!nf) return null;
        return `nf_${nf}`;
    };

    const isCompraSelecionada = (compra) =>
        comprasSelecionadas.some(c => String(c._id) === String(compra._id));

    const podeSelecionarCompra = (compra) => {
        const chaveCompra = getChaveAgrupamentoCompra(compra);
        if (!chaveCompra) return false;

        if (comprasSelecionadas.length === 0) return true;
        if (isCompraSelecionada(compra)) return true;

        const chavePrimeiro = getChaveAgrupamentoCompra(comprasSelecionadas[0]);
        if (!chavePrimeiro) return false;

        return chavePrimeiro === chaveCompra;
    };

    // =============================================
    // FETCH — CONFIGURAÇÃO
    // =============================================
    const fetchConfiguracao = useCallback(async () => {
        try {
            const res = await api.get(`/recebimentos/configuracao?_t=${Date.now()}`);
            if (res.data?.success) {
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar configuração da empresa:', err);
        }
    }, [api, aplicarConfiguracao]);

    // =============================================
    // FETCH — CONTAS
    // =============================================
    const fetchContas = useCallback(async () => {
        try {
            const res = await api.get(`/recebimentos/contas?_t=${Date.now()}`);
            if (res.data?.success) {
                setContasDisponiveis(res.data.data);
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar contas:', err);
        }
    }, [api, aplicarConfiguracao]);

    // =============================================
    // FETCH — SALDOS
    // =============================================
    const fetchSaldos = useCallback(async () => {
        try {
            const res = await api.get(`/recebimentos/saldos?_t=${Date.now()}`);
            if (res.data?.success) {
                setSaldos(res.data.data);
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar saldos:', err);
        }
    }, [api, aplicarConfiguracao]);

    // =============================================
    // FETCH — RESUMO DIA
    // =============================================
    const fetchResumoDia = useCallback(async () => {
        try {
            const res = await api.get(`/recebimentos/resumo-dia?_t=${Date.now()}`);
            if (res.data?.success) {
                setResumoDia(res.data.data);
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar resumo do dia:', err);
        }
    }, [api, aplicarConfiguracao]);

    // =============================================
    // FETCH — LISTA DE RECEBIMENTOS
    // =============================================
    const fetchRecebimentos = useCallback(async (showLoading = true, pageOverride = null) => {
        if (showLoading) setTableLoading(true);
        setLoading(true);
        const currentPage = pageOverride !== null ? pageOverride : pagination.page;

        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('limit', pagination.limit);
            params.append('_t', Date.now());
            if (searchTerm) params.append('search', searchTerm);
            if (filtroTipo !== 'todos') params.append('tipo', filtroTipo);
            if (filtroStatus !== 'todos') params.append('status', filtroStatus);
            if (filtroMetodo !== 'todos') params.append('metodoPagamento', filtroMetodo);
            if (filtroDataInicio) params.append('dataInicio', filtroDataInicio);
            if (filtroDataFim) params.append('dataFim', filtroDataFim);

            const res = await api.get(`/recebimentos?${params.toString()}`);

            if (res.data?.success) {
                setRecebimentos(res.data.data || []);
                if (res.data.pagination) setPagination({ ...res.data.pagination, page: currentPage });
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao listar:', err);
            showToast('Erro ao carregar recebimentos', 'danger');
            setRecebimentos([]);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    }, [api, pagination.page, pagination.limit, searchTerm, filtroTipo, filtroStatus, filtroMetodo, filtroDataInicio, filtroDataFim, aplicarConfiguracao]);

    // =============================================
    // FETCH — RECIBOS ELEGÍVEIS
    // =============================================
    const fetchRecibosElegiveis = useCallback(async (search = '') => {
        setLoadingRecibos(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', '200');
            params.append('_t', Date.now());
            if (search && search.trim()) params.append('search', search.trim());

            const res = await api.get('/recebimentos/recibos-elegiveis?' + params.toString());

            if (res.data?.success) {
                setRecibosElegiveis(res.data.data || []);
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar recibos elegíveis:', err);
            setRecibosElegiveis([]);
        } finally {
            setLoadingRecibos(false);
        }
    }, [api, aplicarConfiguracao]);

    // =============================================
    // FETCH COMPRAS DISPONÍVEIS
    // 🔥 v20 — o backend já exclui as compras ligadas a recebimentos ATIVOS
    // =============================================
    const fetchComprasDisponiveis = useCallback(async (search = '') => {
        setLoadingCompras(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', '200');
            params.append('apenasParaSangria', 'true');
            params.append('_t', Date.now());
            if (search && search.trim()) params.append('search', search.trim());

            const res = await api.get(`/compras-fornecedor?${params.toString()}`);

            if (res.data?.success) {
                const compras = res.data.data || [];

                const comprasNormalizadas = compras.map(c => {
                    const total = extrairValorNumerico(c.total);
                    const valorPago = extrairValorNumerico(c.valorPago);

                    let saldoDevedor = extrairValorNumerico(c.saldoDevedor);
                    let saldoCalculado = extrairValorNumerico(c.saldoDevedorCalculado);

                    if (saldoDevedor === 0 && saldoCalculado === 0 && total > 0) {
                        saldoDevedor = Math.max(0, total - valorPago);
                        saldoCalculado = saldoDevedor;
                    }
                    if (saldoCalculado === 0 && saldoDevedor > 0) {
                        saldoCalculado = saldoDevedor;
                    }

                    const documentoFiscal = c.documentoFiscal
                        ? String(c.documentoFiscal).trim()
                        : '';

                    const nfNormalizada = normalizarNF(documentoFiscal);
                    const itens = extrairItensCompra(c);

                    return {
                        ...c,
                        total,
                        valorPago,
                        saldoDevedor,
                        saldoDevedorCalculado: saldoCalculado || Math.max(0, total - valorPago),
                        documentoFiscal,
                        nfNormalizada,
                        itens
                    };
                });

                setComprasDisponiveis(comprasNormalizadas);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar compras disponíveis:', err);
            setComprasDisponiveis([]);
        } finally {
            setLoadingCompras(false);
        }
    }, [api]);

    // =============================================
    // CARREGAR TUDO
    // =============================================
    const carregarTudo = useCallback(async () => {
        await fetchConfiguracao();
        await Promise.all([
            fetchContas(),
            fetchSaldos(),
            fetchResumoDia(),
            fetchRecebimentos(true, 1)
        ]);
    }, [fetchConfiguracao, fetchContas, fetchSaldos, fetchResumoDia, fetchRecebimentos]);

    useEffect(() => {
        carregarTudo();
    }, []);

    // =============================================
    // FECHAR DROPDOWNS AO CLICAR FORA
    // =============================================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRecibosRef.current &&
                !dropdownRecibosRef.current.contains(event.target)
            ) {
                setShowRecibosDropdown(false);
            }
            if (
                dropdownComprasRef.current &&
                !dropdownComprasRef.current.contains(event.target)
            ) {
                setShowComprasDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // =============================================
    // DEBOUNCE DA PESQUISA DE RECIBOS
    // =============================================
    useEffect(() => {
        if (!showRecibosDropdown) return;

        if (searchReciboTimeoutRef.current) {
            clearTimeout(searchReciboTimeoutRef.current);
        }

        searchReciboTimeoutRef.current = setTimeout(() => {
            fetchRecibosElegiveis(searchRecibo);
        }, 350);

        return () => {
            if (searchReciboTimeoutRef.current) {
                clearTimeout(searchReciboTimeoutRef.current);
            }
        };
    }, [searchRecibo, showRecibosDropdown, fetchRecibosElegiveis]);

    // =============================================
    // 🔥 v21 — Quando tipo muda, ajustar compras e recibos
    // =============================================
    useEffect(() => {
        // Sangria → carregar compras
        if (formData.tipo === 'Sangria' && showCreateModal) {
            fetchComprasDisponiveis(searchCompra);
        } else {
            if (formData.tipo !== 'Sangria' && comprasSelecionadas.length > 0) {
                setComprasSelecionadas([]);
                setFormData(prev => ({ ...prev, compraFornecedorId: null }));
            }
        }

        // 🔥 Recibos só fazem sentido no tipo "Entrada"
        if (formData.tipo !== 'Entrada' && recibosSelecionados.length > 0) {
            setRecibosSelecionados([]);
            setMetodosDetectados([]);
            setSearchRecibo('');
            setShowRecibosDropdown(false);
            setFormData(prev => ({
                ...prev,
                valor: comprasSelecionadas.length > 0
                    ? comprasSelecionadas.reduce((acc, c) => acc + extrairValorNumerico(c.total), 0)
                    : 0,
                referencia: '',
                metodoPagamento: 'Dinheiro'
            }));
        }
    }, [formData.tipo, showCreateModal, searchCompra, fetchComprasDisponiveis]);

    // =============================================
    // AUTO-SELECIONAR BANCO PRINCIPAL
    // =============================================
    useEffect(() => {
        if (!contasDisponiveis.bancos || contasDisponiveis.bancos.length === 0) return;

        const bancoPrincipal = contasDisponiveis.bancos.find(b => b.principal === true);
        const bancoUnico = contasDisponiveis.bancos.length === 1 ? contasDisponiveis.bancos[0] : null;
        const sugerido = bancoPrincipal || bancoUnico;

        if (sugerido && !formData.contaBancoId) {
            setFormData(prev => ({
                ...prev,
                contaBancoId: sugerido._id || ''
            }));
        }
    }, [contasDisponiveis.bancos]);

    // =============================================
    // TOGGLE COMPRA
    // =============================================
    const toggleCompraSelecionada = (compra) => {
        setComprasSelecionadas(prev => {
            const existe = prev.find(c => String(c._id) === String(compra._id));
            let novaLista;

            if (existe) {
                novaLista = prev.filter(c => String(c._id) !== String(compra._id));
            } else {
                novaLista = [...prev, compra];
            }

            const totalGeral = novaLista.reduce((acc, c) => {
                return acc + extrairValorNumerico(c.total);
            }, 0);

            const referencias = novaLista
                .map(c => {
                    const ref = c.referenciaPagamento || '';
                    const num = c.numero || '';
                    return ref ? `${num}: ${ref}` : null;
                })
                .filter(Boolean);

            const metodoDaCompra = novaLista[0]?.metodoPagamento
                ? (novaLista[0].metodoPagamento === 'Transferência'
                    ? 'Transferência Bancária'
                    : novaLista[0].metodoPagamento)
                : null;

            setFormData(prevForm => ({
                ...prevForm,
                valor: totalGeral,
                compraFornecedorId: novaLista.length > 0 ? novaLista[0]._id : null,
                referencia: referencias.length > 0
                    ? referencias.join(' | ')
                    : prevForm.referencia,
                metodoPagamento: metodoDaCompra && METODOS_PAGAMENTO.some(m => m.value === metodoDaCompra)
                    ? metodoDaCompra
                    : prevForm.metodoPagamento
            }));

            return novaLista;
        });
    };

    const removerTodasCompras = () => {
        setComprasSelecionadas([]);
        setFormData(prev => ({ ...prev, compraFornecedorId: null, valor: 0 }));
    };

    // =============================================
    // CRIAR RECEBIMENTO
    // =============================================
    const handleCreate = async (e) => {
        e.preventDefault();

        if (!formData.valor || formData.valor <= 0) {
            showToast('Informe um valor válido', 'danger');
            return;
        }

        if (formData.contaTipo === 'Banco' && !formData.contaBancoId) {
            showToast('Selecione um banco', 'danger');
            return;
        }

        if (formData.contaTipo === 'Caixa' && !formData.contaCaixaNome.trim()) {
            showToast('Informe o nome da caixa', 'danger');
            return;
        }

        if (formData.contaTipo === 'Mobile' && !formData.contaOperadora.trim()) {
            showToast('Selecione a operadora', 'danger');
            return;
        }

        if (formData.metodoPagamento !== 'Dinheiro' && !formData.referencia.trim()) {
            showToast('Informe a referência do pagamento', 'warning');
            return;
        }

        const contaDestino = { tipo: formData.contaTipo };

        if (formData.contaTipo === 'Caixa') {
            contaDestino.caixaNome = formData.contaCaixaNome.trim();
            contaDestino.caixaId = formData.contaCaixaNome.trim().toLowerCase().replace(/\s+/g, '-');
        } else if (formData.contaTipo === 'Banco') {
            const banco = contasDisponiveis.bancos.find(b => String(b._id) === String(formData.contaBancoId));
            contaDestino.bancoId = formData.contaBancoId;
            contaDestino.bancoNome = banco?.nome || '';
            contaDestino.conta = banco?.conta || '';
            contaDestino.nib = banco?.nib || '';
            contaDestino.swift = banco?.swift || '';
            contaDestino.titular = banco?.titular || '';
        } else if (formData.contaTipo === 'Mobile') {
            contaDestino.operadora = formData.contaOperadora.trim();
            contaDestino.numeroTelefone = formData.contaNumeroTelefone.trim();
        }

        let metodosDetalhadosFinal = [];

        // 🔥 v21 — Recibos só são aplicados quando tipo=Entrada
        if (formData.tipo === 'Entrada') {
            if (metodosDetectados.length > 0) {
                metodosDetalhadosFinal = metodosDetectados.map(m => ({
                    reciboId: m.reciboId,
                    numeroRecibo: m.numero,
                    faturaNumero: m.faturaNumero,
                    metodoPagamento: m.metodoPagamento,
                    referencia: m.referencia,
                    valor: extrairValorNumerico(m.valorPago),
                    dataPagamento: m.dataPagamento
                }));
            } else if (recibosSelecionados.length > 0) {
                const metodosUnicos = [...new Set(recibosSelecionados.map(r => r.metodoPagamento || 'Dinheiro'))];
                if (metodosUnicos.length > 1) {
                    metodosDetalhadosFinal = recibosSelecionados.map(r => ({
                        reciboId: r._id,
                        numeroRecibo: r.numero || '',
                        faturaNumero: r.faturaSnapshot?.numero || '',
                        metodoPagamento: r.metodoPagamento || 'Dinheiro',
                        referencia: r.referenciaPagamento || '',
                        valor: extrairValorNumerico(r.valorPago),
                        dataPagamento: r.dataPagamento
                    }));
                }
            }
        }

        try {
            setLoading(true);

            const comprasSnapshot = comprasSelecionadas.map(c => {
                const itens = extrairItensCompra(c);

                return {
                    _id: c._id,
                    numero: c.numero || '',
                    fornecedorNome: c.fornecedorNome || c.fornecedorId?.nome || '',
                    documentoFiscal: c.documentoFiscal || '',
                    dataCompra: c.dataCompra,
                    total: extrairValorNumerico(c.total),
                    valorPago: extrairValorNumerico(c.valorPago),
                    saldoDevedor: extrairValorNumerico(c.saldoDevedorCalculado || c.saldoDevedor),
                    metodoPagamento: c.metodoPagamento || '',
                    referenciaPagamento: c.referenciaPagamento || '',
                    itens
                };
            });

            const payload = {
                tipo: formData.tipo,
                valor: parseFloat(formData.valor),
                metodoPagamento: formData.metodoPagamento,
                referencia: formData.referencia,
                data: formData.data,
                observacoes: formData.observacoes,
                contaDestino,
                metodosDetalhados: metodosDetalhadosFinal,
                compraFornecedorId: comprasSelecionadas.length > 0 ? comprasSelecionadas[0]._id : null,
                comprasFornecedorIds: comprasSelecionadas.map(c => c._id),
                comprasSnapshot,
                origem: {
                    tipo: (formData.tipo === 'Entrada' && recibosSelecionados.length > 0)
                        ? 'Recibo'
                        : (comprasSelecionadas.length > 0 ? 'CompraFornecedor' : 'Manual'),
                    recibosIds: (formData.tipo === 'Entrada')
                        ? recibosSelecionados.map(r => r._id)
                        : [],
                    comprasIds: comprasSelecionadas.map(c => c._id),
                    faturasIds: [],
                    descricao: (formData.tipo === 'Entrada' && recibosSelecionados.length > 0)
                        ? `Agrupamento de ${recibosSelecionados.length} recibo(s)`
                        : (comprasSelecionadas.length > 0
                            ? `Pagamento de ${comprasSelecionadas.length} compra(s) a fornecedor (NF ${comprasSelecionadas[0].documentoFiscal || '—'})`
                            : '')
                }
            };

            const res = await api.post('/recebimentos', payload);

            if (res.data?.success) {
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);

                const msgSucesso = comprasSelecionadas.length > 0
                    ? `Recebimento registado e ${comprasSelecionadas.length} compra(s) atualizada(s)!`
                    : `Recebimento registado com sucesso!`;

                showToast(msgSucesso, 'success');
                setShowCreateModal(false);
                resetForm();
                await carregarTudo();
            }
        } catch (err) {
            console.error('❌ Erro ao criar:', err);
            const msgErro = err.response?.data?.erro
                || err.response?.data?.detalhe
                || 'Erro ao criar recebimento';
            showToast(msgErro, 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // SELECIONAR RECIBO
    // =============================================
    const toggleReciboSelecionado = (recibo) => {
        if (!isReciboSelecionado(recibo) && !podeSelecionarRecibo(recibo)) {
            showToast('Não é possível agrupar recibos de faturas diferentes. Limpe a seleção primeiro.', 'warning');
            return;
        }

        setRecibosSelecionados(prev => {
            const existe = prev.find(r => r._id === recibo._id);
            let novaLista;
            if (existe) {
                novaLista = prev.filter(r => r._id !== recibo._id);
            } else {
                novaLista = [...prev, recibo];
            }

            const totalSelecionado = novaLista.reduce((acc, r) => {
                return acc + extrairValorNumerico(r.valorPago);
            }, 0);

            if (novaLista.length === 0) {
                setFormData(prev => ({
                    ...prev,
                    valor: totalSelecionado,
                    metodoPagamento: 'Dinheiro',
                    referencia: ''
                }));
                setMetodosDetectados([]);
            }
            else if (novaLista.length === 1) {
                const r = novaLista[0];
                setFormData(prev => ({
                    ...prev,
                    valor: totalSelecionado,
                    metodoPagamento: r.metodoPagamento || 'Dinheiro',
                    referencia: r.referenciaPagamento || ''
                }));
                setMetodosDetectados([]);
            }
            else {
                const metodos = novaLista.map(r => r.metodoPagamento || 'Dinheiro');
                const metodosUnicos = [...new Set(metodos)];

                if (metodosUnicos.length === 1) {
                    const metodoUnico = metodosUnicos[0];
                    const referencias = novaLista
                        .map(r => {
                            const ref = r.referenciaPagamento || '';
                            const num = r.numero || '';
                            return ref ? `${num}: ${ref}` : null;
                        })
                        .filter(Boolean);

                    setFormData(prev => ({
                        ...prev,
                        valor: totalSelecionado,
                        metodoPagamento: metodoUnico,
                        referencia: referencias.length > 0 ? referencias.join(' | ') : ''
                    }));
                    setMetodosDetectados([]);
                } else {
                    const listaDetalhada = novaLista.map(r => ({
                        reciboId: r._id,
                        numero: r.numero || '',
                        metodoPagamento: r.metodoPagamento || 'Dinheiro',
                        referencia: r.referenciaPagamento || '',
                        valorPago: extrairValorNumerico(r.valorPago),
                        faturaNumero: r.faturaSnapshot?.numero || '—',
                        dataPagamento: r.dataPagamento
                    }));

                    setMetodosDetectados(listaDetalhada);
                    setShowMetodoModal(true);

                    const metodosTexto = listaDetalhada
                        .map(m => `${m.numero}: ${m.metodoPagamento}${m.referencia ? ` (${m.referencia})` : ''}`)
                        .join(' | ');

                    setFormData(prev => ({
                        ...prev,
                        valor: totalSelecionado,
                        metodoPagamento: listaDetalhada[0].metodoPagamento,
                        referencia: metodosTexto
                    }));
                }
            }

            return novaLista;
        });
    };

    // =============================================
    // APLICAR MÉTODO
    // =============================================
    const aplicarMetodoEscolhido = (metodoItem) => {
        const referenciasIdentificadas = metodosDetectados
            .map(m => `${m.numero}: ${m.metodoPagamento}${m.referencia ? ` (${m.referencia})` : ''}`)
            .join(' | ');

        setFormData(prev => ({
            ...prev,
            metodoPagamento: metodoItem.metodoPagamento,
            referencia: referenciasIdentificadas
        }));

        setShowMetodoModal(false);
        showToast(`Método principal "${metodoItem.metodoPagamento}" — todos os métodos aplicados`, 'success');
    };

    const aplicarTodasReferencias = () => {
        const metodoPrincipal = metodosDetectados[0]?.metodoPagamento || 'Dinheiro';
        const referenciasIdentificadas = metodosDetectados
            .map(m => `${m.numero}: ${m.metodoPagamento}${m.referencia ? ` (${m.referencia})` : ''}`)
            .join(' | ');

        setFormData(prev => ({
            ...prev,
            metodoPagamento: metodoPrincipal,
            referencia: referenciasIdentificadas
        }));

        setShowMetodoModal(false);
        showToast(`Todos os ${metodosDetectados.length} métodos aplicados`, 'success');
    };

    const totalSelecionado = useMemo(() => {
        return recibosSelecionados.reduce((acc, r) => {
            return acc + extrairValorNumerico(r.valorPago);
        }, 0);
    }, [recibosSelecionados]);

    const totalComprasSelecionadas = useMemo(() => {
        return comprasSelecionadas.reduce((acc, c) => {
            return acc + extrairValorNumerico(c.total);
        }, 0);
    }, [comprasSelecionadas]);

    const totalSaldoComprasSelecionadas = useMemo(() => {
        return comprasSelecionadas.reduce((acc, c) => {
            return acc + extrairValorNumerico(c.saldoDevedorCalculado || c.saldoDevedor);
        }, 0);
    }, [comprasSelecionadas]);

    const recibosFiltrados = useMemo(() => {
        if (!searchRecibo.trim()) return recibosElegiveis;
        const s = searchRecibo.toLowerCase().trim();
        return recibosElegiveis.filter(r => {
            return (
                String(r.numero || '').toLowerCase().includes(s) ||
                String(r.clienteInfo?.nome || '').toLowerCase().includes(s) ||
                String(r.faturaSnapshot?.numero || '').toLowerCase().includes(s)
            );
        });
    }, [recibosElegiveis, searchRecibo]);

    const comprasFiltradas = useMemo(() => {
        if (!searchCompra.trim()) return comprasDisponiveis;
        const s = searchCompra.toLowerCase().trim();
        return comprasDisponiveis.filter(c => {
            return (
                String(c.numero || '').toLowerCase().includes(s) ||
                String(c.fornecedorNome || '').toLowerCase().includes(s) ||
                String(c.documentoFiscal || '').toLowerCase().includes(s) ||
                String(c.referenciaPagamento || '').toLowerCase().includes(s)
            );
        });
    }, [comprasDisponiveis, searchCompra]);

    // =============================================
    // 🔥 v20 — PAGINAÇÃO DAS LISTAS DOS DROPDOWNS
    // =============================================
    const comprasPaginadas = useMemo(() => {
        const inicio = (paginaCompras - 1) * ITENS_POR_PAGINA_DROPDOWN;
        return comprasFiltradas.slice(inicio, inicio + ITENS_POR_PAGINA_DROPDOWN);
    }, [comprasFiltradas, paginaCompras]);

    const totalPaginasCompras = Math.max(
        1,
        Math.ceil(comprasFiltradas.length / ITENS_POR_PAGINA_DROPDOWN)
    );

    const recibosPaginados = useMemo(() => {
        const inicio = (paginaRecibos - 1) * ITENS_POR_PAGINA_DROPDOWN;
        return recibosFiltrados.slice(inicio, inicio + ITENS_POR_PAGINA_DROPDOWN);
    }, [recibosFiltrados, paginaRecibos]);

    const totalPaginasRecibos = Math.max(
        1,
        Math.ceil(recibosFiltrados.length / ITENS_POR_PAGINA_DROPDOWN)
    );

    // Reset da página quando a pesquisa muda
    useEffect(() => {
        setPaginaCompras(1);
    }, [searchCompra, comprasDisponiveis]);

    useEffect(() => {
        setPaginaRecibos(1);
    }, [searchRecibo, recibosElegiveis]);

    // =============================================
    // 🔥 v20 — COMPONENTE DE PAGINAÇÃO DO DROPDOWN
    // =============================================
    const renderPaginacaoDropdown = (paginaAtual, totalPaginas, setPagina) => {
        if (totalPaginas <= 1) return null;

        const numeros = gerarNumerosPagina(paginaAtual, totalPaginas);

        return (
            <div className="rb-dropdown-paginacao">
                <button
                    type="button"
                    className="rb-dropdown-paginacao-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setPagina(Math.max(1, paginaAtual - 1));
                    }}
                    disabled={paginaAtual === 1}
                >
                    <Icon name="chevron_left" size={14} />
                </button>

                {numeros.map((p, i) => {
                    if (p === '...') {
                        return (
                            <span
                                key={`dots-${i}`}
                                className="rb-dropdown-paginacao-dots"
                            >
                                …
                            </span>
                        );
                    }
                    return (
                        <button
                            key={p}
                            type="button"
                            className={`rb-dropdown-paginacao-num ${paginaAtual === p ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPagina(p);
                            }}
                        >
                            {p}
                        </button>
                    );
                })}

                <button
                    type="button"
                    className="rb-dropdown-paginacao-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setPagina(Math.min(totalPaginas, paginaAtual + 1));
                    }}
                    disabled={paginaAtual === totalPaginas}
                >
                    <Icon name="chevron_right" size={14} />
                </button>
            </div>
        );
    };

    // =============================================
    // EXPORTAR CSV
    // =============================================
    const exportarCSV = async () => {
        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filtroTipo !== 'todos') params.append('tipo', filtroTipo);
            if (filtroStatus !== 'todos') params.append('status', filtroStatus);
            if (filtroMetodo !== 'todos') params.append('metodoPagamento', filtroMetodo);
            if (filtroDataInicio) params.append('dataInicio', filtroDataInicio);
            if (filtroDataFim) params.append('dataFim', filtroDataFim);

            const res = await api.get(`/recebimentos/export?${params.toString()}`, {
                responseType: 'blob'
            });

            const nomeFicheiro = `Recebimento_geral_${gerarTimestampFicheiro()}.csv`;

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeFicheiro);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showToast('CSV exportado com sucesso!', 'success');
        } catch (err) {
            console.error('❌ Erro ao exportar CSV:', err);
            showToast('Erro ao exportar CSV', 'danger');
        }
    };

    // =============================================
    // EXPORTAR PDF (Lista filtrada)
    // =============================================
    const exportarPDFLista = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filtroTipo !== 'todos') params.append('tipo', filtroTipo);
            if (filtroStatus !== 'todos') params.append('status', filtroStatus);
            if (filtroMetodo !== 'todos') params.append('metodoPagamento', filtroMetodo);
            if (filtroDataInicio) params.append('dataInicio', filtroDataInicio);
            if (filtroDataFim) params.append('dataFim', filtroDataFim);

            const res = await api.get(`/recebimentos/export/pdf?${params.toString()}`, {
                responseType: 'blob'
            });

            const nomeFicheiro = `Recebimento_geral_${gerarTimestampFicheiro()}.pdf`;

            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeFicheiro);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showToast('PDF exportado com sucesso!', 'success');
        } catch (err) {
            console.error('❌ Erro ao exportar PDF:', err);
            showToast('Erro ao exportar PDF', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // EXPORTAR PDF (individual)
    // =============================================
    const exportarPDF = async (recebimento) => {
        if (recebimento.status === 'Anulado') {
            showToast('Não é possível exportar PDF de um recebimento anulado.', 'warning');
            return;
        }

        try {
            setLoading(true);
            const res = await api.get(`/recebimentos/${recebimento._id}/pdf`, {
                responseType: 'blob'
            });

            const nomeFicheiro = `Recebimento_${recebimento.numero}.pdf`;

            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeFicheiro);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showToast('PDF exportado com sucesso!', 'success');
        } catch (err) {
            console.error('❌ Erro ao exportar PDF:', err);
            showToast('Erro ao exportar PDF', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // ANULAR / EXCLUIR
    // =============================================
    const anularRecebimento = async () => {
        if (!motivoAnulacao.trim()) {
            showToast('Informe o motivo da anulação', 'warning');
            return;
        }
        try {
            setLoading(true);
            const res = await api.post(
                `/recebimentos/${recebimentoToAnular._id}/anular`,
                { motivo: motivoAnulacao }
            );
            if (res.data?.success) {
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);

                showToast('Recebimento anulado com sucesso!', 'success');
                setShowAnularModal(false);
                setRecebimentoToAnular(null);
                setMotivoAnulacao('');

                await carregarTudo();

                if (showCreateModal) {
                    await fetchRecibosElegiveis(searchRecibo);
                }

                if (recebimentoDetalhes?._id === recebimentoToAnular._id) {
                    setShowDetailsModal(false);
                }
            }
        } catch (err) {
            console.error('❌ Erro ao anular:', err);
            showToast(err.response?.data?.erro || 'Erro ao anular', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const res = await api.delete(`/recebimentos/${recebimentoToDelete._id}`);
            if (res.data?.success) {
                showToast('Recebimento excluído com sucesso!', 'success');
                setShowDeleteModal(false);
                setRecebimentoToDelete(null);
                await carregarTudo();
            }
        } catch (err) {
            console.error('❌ Erro ao excluir:', err);
            showToast(err.response?.data?.erro || 'Erro ao excluir', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // VER DETALHES
    // =============================================
    const verDetalhes = async (recebimento) => {
        try {
            setLoading(true);
            const res = await api.get(`/recebimentos/${recebimento._id}?_t=${Date.now()}`);
            if (res.data?.success) {
                aplicarConfiguracao(res.data.configuracao, res.data.empresa);

                const detalhes = { ...res.data.data };
                if (Array.isArray(detalhes.metodosDetalhados)) {
                    detalhes.metodosDetalhados = detalhes.metodosDetalhados.map(m => ({
                        ...m,
                        valor: extrairValorNumerico(m.valor ?? m.valorPago ?? m.valorRecebido)
                    }));
                }

                if (detalhes.compraFornecedorId && typeof detalhes.compraFornecedorId === 'object') {
                    const cf = detalhes.compraFornecedorId;
                    cf.total = extrairValorNumerico(cf.total);
                    cf.valorPago = extrairValorNumerico(cf.valorPago);
                    cf.saldoDevedor = extrairValorNumerico(cf.saldoDevedor);
                    cf.saldoDevedorCalculado = cf.saldoDevedor > 0
                        ? cf.saldoDevedor
                        : Math.max(0, cf.total - cf.valorPago);
                    cf.documentoFiscal = cf.documentoFiscal
                        ? String(cf.documentoFiscal).trim()
                        : '';
                }

                if (Array.isArray(detalhes.comprasFornecedorIds)) {
                    detalhes.comprasFornecedorIds = detalhes.comprasFornecedorIds.map(cf => {
                        if (typeof cf === 'object' && cf !== null) {
                            const total = extrairValorNumerico(cf.total);
                            const valorPago = extrairValorNumerico(cf.valorPago);
                            return {
                                ...cf,
                                total,
                                valorPago,
                                saldoDevedor: extrairValorNumerico(cf.saldoDevedor),
                                saldoDevedorCalculado: Math.max(0, total - valorPago),
                                documentoFiscal: cf.documentoFiscal ? String(cf.documentoFiscal).trim() : ''
                            };
                        }
                        return cf;
                    });
                }

                if (Array.isArray(detalhes.comprasSnapshot)) {
                    detalhes.comprasSnapshot = detalhes.comprasSnapshot.map(c => ({
                        ...c,
                        total: extrairValorNumerico(c.total),
                        valorPago: extrairValorNumerico(c.valorPago),
                        saldoDevedor: extrairValorNumerico(c.saldoDevedor),
                        itens: Array.isArray(c.itens) ? c.itens.map(it => ({
                            produtoNome: it.produtoNome || 'Produto',
                            quantidade: extrairValorNumerico(it.quantidade),
                            precoUnitario: extrairValorNumerico(it.precoUnitario),
                            subtotal: extrairValorNumerico(it.subtotal),
                            unidade: it.unidade || ''
                        })) : []
                    }));
                }

                if ((!detalhes.comprasSnapshot || detalhes.comprasSnapshot.length === 0) &&
                    Array.isArray(detalhes.comprasFornecedorIds)) {
                    detalhes.comprasSnapshot = detalhes.comprasFornecedorIds
                        .filter(c => typeof c === 'object' && c !== null)
                        .map(c => ({
                            _id: c._id,
                            numero: c.numero || '',
                            fornecedorNome: c.fornecedorNome || c.fornecedorId?.nome || '',
                            documentoFiscal: c.documentoFiscal ? String(c.documentoFiscal).trim() : '',
                            dataCompra: c.dataCompra,
                            total: extrairValorNumerico(c.total),
                            valorPago: extrairValorNumerico(c.valorPago),
                            saldoDevedor: extrairValorNumerico(c.saldoDevedorCalculado || c.saldoDevedor),
                            metodoPagamento: c.metodoPagamento || '',
                            referenciaPagamento: c.referenciaPagamento || '',
                            itens: extrairItensCompra(c)
                        }));
                }

                setRecebimentoDetalhes(detalhes);
                setShowDetailsModal(true);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar detalhes:', err);
            showToast('Erro ao carregar detalhes', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // RESET
    // =============================================
    const resetForm = () => {
        setFormData({
            tipo: 'Entrada',
            valor: 0,
            metodoPagamento: 'Dinheiro',
            referencia: '',
            data: new Date().toISOString().split('T')[0],
            observacoes: '',
            contaTipo: 'Caixa',
            contaCaixaNome: 'Caixa Principal',
            contaBancoId: '',
            contaOperadora: 'M-Pesa',
            contaNumeroTelefone: '',
            compraFornecedorId: null
        });
        setRecibosSelecionados([]);
        setComprasSelecionadas([]);
        setSearchRecibo('');
        setShowRecibosDropdown(false);
        setShowMetodoModal(false);
        setMetodosDetectados([]);
        setSearchCompra('');
        setShowComprasDropdown(false);
        setPaginaCompras(1);
        setPaginaRecibos(1);
    };

    const abrirModalCriar = async () => {
        resetForm();
        await fetchRecibosElegiveis();
        await fetchContas();
        setShowCreateModal(true);
    };

    // =============================================
    // FILTROS / PAGINAÇÃO
    // =============================================
    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRecebimentos(true, 1);
    };

    const limparFiltros = () => {
        setSearchTerm('');
        setFiltroTipo('todos');
        setFiltroStatus('todos');
        setFiltroMetodo('todos');
        setFiltroDataInicio('');
        setFiltroDataFim('');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => fetchRecebimentos(true, 1), 100);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
        fetchRecebimentos(true, page);
    };

    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        return (
            <div className="rb-pagination">
                <div className="rb-pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} recebimentos</span>
                </div>
                <div className="rb-pagination-controls">
                    <button className="rb-pagination-btn" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="rb-pagination-numbers">
                        {gerarNumerosPagina(page, pages).map((p, i) => {
                            if (p === '...') return <span key={`d-${i}`} className="rb-pagination-dots">…</span>;
                            return (
                                <button key={p} className={`rb-pagination-number ${page === p ? 'active' : ''}`} onClick={() => goToPage(p)}>
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button className="rb-pagination-btn" onClick={() => goToPage(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // =============================================
    // RENDER
    // =============================================
    return (
        <div className="rb-container">

            {/* HEADER */}
            <div className="rb-header">
                <div className="rb-header-title">
                    <h2><Icon name="account_balance_wallet" size={24} /> Recebimentos</h2>
                    <p>
                        Painel de tesouraria — entradas, sangrias e reforços
                        {empresaInfo?.nome && (
                            <span style={{ marginLeft: '0.5rem', opacity: 0.7, fontSize: '0.75rem' }}>
                                • {empresaInfo.nome}
                            </span>
                        )}
                    </p>
                </div>
                <div className="rb-header-actions">
                    <button className="rb-btn-refresh" onClick={carregarTudo} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                    <button className="rb-btn-export" onClick={exportarCSV} title="Exportar CSV">
                        <Icon name="grid_on" size={18} /> CSV
                    </button>
                    <button className="rb-btn-export rb-btn-export-pdf" onClick={exportarPDFLista} title="Exportar PDF">
                        <Icon name="picture_as_pdf" size={18} /> PDF
                    </button>
                    <button className="rb-btn-create" onClick={abrirModalCriar}>
                        <Icon name="add" size={18} /> Novo Recebimento
                    </button>
                </div>
            </div>

            {/* SALDOS */}
            {saldos?.contas && saldos.contas.length > 0 && (
                <div className="rb-saldos-section">
                    <div className="rb-saldos-header">
                        <h3 className="rb-section-title">
                            <Icon name="account_balance" size={16} />
                            Saldos por Conta
                        </h3>
                    </div>
                    <div className="rb-saldos-grid">
                        {saldos.contas.map((conta, idx) => (
                            <div key={idx} className={`rb-saldo-card rb-saldo-${(conta.tipo || 'caixa').toLowerCase()}`}>
                                <div className="rb-saldo-icon">
                                    <Icon name={getContaIcon(conta.tipo)} size={18} />
                                </div>
                                <div className="rb-saldo-info">
                                    <span className="rb-saldo-label">{conta.label || conta.tipo}</span>
                                    <span className={`rb-saldo-value ${conta.saldo >= 0 ? 'positive' : 'negative'}`}>
                                        {formatarMoeda(conta.saldo)}
                                    </span>
                                    <span className="rb-saldo-detail">
                                        {conta.count} movimento{conta.count !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* FILTROS */}
            <div className="rb-filters">
                <div className="rb-search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar (nº, referência, operador)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && aplicarFiltros()}
                    />
                    {searchTerm && (
                        <button className="rb-search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="rb-filter-actions">
                    <select className="rb-filter-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                        <option value="todos">Todos os Tipos</option>
                        {TIPOS_MOVIMENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <select className="rb-filter-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                        <option value="todos">Todos os Status</option>
                        {STATUS_RECEBIMENTO.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <select className="rb-filter-select" value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}>
                        <option value="todos">Todos os Métodos</option>
                        {METODOS_PAGAMENTO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <input type="date" className="rb-filter-select" value={filtroDataInicio}
                        onChange={(e) => setFiltroDataInicio(e.target.value)} title="Data Início" />
                    <input type="date" className="rb-filter-select" value={filtroDataFim}
                        onChange={(e) => setFiltroDataFim(e.target.value)} title="Data Fim" />
                    <button className="rb-btn-filter-apply" onClick={aplicarFiltros}>
                        <Icon name="search" size={16} /> Aplicar
                    </button>
                    <button className="rb-btn-filter-clear" onClick={limparFiltros}>
                        <Icon name="clear" size={16} />
                    </button>
                </div>
            </div>

            {/* TABELA */}
            <div className="rb-table-container">
                {tableLoading && (
                    <div className="rb-table-loading-overlay active">
                        <div className="rb-spinner"></div>
                        <span className="rb-loading-text">A carregar recebimentos...</span>
                    </div>
                )}

                <div className="rb-table-wrapper">
                    <table className="rb-table">
                        <thead>
                            <tr>
                                <th className="rb-col-numero">Nº</th>
                                <th className="rb-col-data">Data</th>
                                <th className="rb-col-tipo">Tipo</th>
                                <th className="rb-col-conta">Conta</th>
                                <th className="rb-col-metodo">Método</th>
                                <th className="rb-col-valor" style={{ textAlign: 'right' }}>Valor</th>
                                <th className="rb-col-operador">Operador</th>
                                <th className="rb-col-status" style={{ textAlign: 'center' }}>Status</th>
                                <th className="rb-col-acoes" style={{ textAlign: 'center' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !tableLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={`sk-${i}`} className="rb-skeleton-row">
                                        {Array.from({ length: 9 }).map((_, j) => (
                                            <td key={j}><div className="rb-skeleton-line" /></td>
                                        ))}
                                    </tr>
                                ))
                            ) : recebimentos.length === 0 ? (
                                <tr className="rb-empty-row">
                                    <td colSpan="9">
                                        <div className="rb-empty-state">
                                            <Icon name="account_balance_wallet" size={48} />
                                            <p>Nenhum recebimento encontrado</p>
                                            <button className="rb-btn-create-small" onClick={abrirModalCriar}>
                                                <Icon name="add" size={16} /> Registar primeiro
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : recebimentos.map(rec => {
                                const tipoInfo = getTipoInfo(rec.tipo);
                                const statusInfo = getStatusInfo(rec.status);
                                const isAnulado = rec.status === 'Anulado';
                                const isSangria = rec.tipo === 'Sangria';

                                return (
                                    <tr key={rec._id} className={`rb-row ${getLinhaClasse(rec.tipo, rec.status)}`}>
                                        <td className="rb-col-numero">
                                            <span className="rb-numero-badge">{rec.numero}</span>
                                        </td>
                                        <td className="rb-col-data">
                                            <span className="rb-validade-badge">{formatarData(rec.data)}</span>
                                        </td>
                                        <td className="rb-col-tipo">
                                            <span className={`rb-status-badge ${tipoInfo.classe}`}>
                                                <Icon name={tipoInfo.icon} size={12} />
                                                {tipoInfo.label}
                                            </span>
                                        </td>
                                        <td className="rb-col-conta">
                                            <div className="rb-conta-cell">
                                                <Icon name={getContaIcon(rec.contaDestino?.tipo)} size={14} />
                                                <span>{getContaLabel(rec.contaDestino)}</span>
                                            </div>
                                        </td>
                                        <td className="rb-col-metodo" style={{ textAlign: 'center' }}>
                                            <span className="rb-metodo-tag">
                                                {rec.metodoPagamento || '---'}
                                            </span>
                                        </td>
                                        <td className="rb-col-valor" style={{ textAlign: 'right' }}>
                                            <strong className="rb-valor"
                                                style={{
                                                    color: isSangria ? '#dc2626' : '#059669',
                                                    textDecoration: isAnulado ? 'line-through' : 'none'
                                                }}>
                                                {isSangria ? '− ' : '+ '}
                                                {formatarMoeda(rec.valor)}
                                            </strong>
                                        </td>
                                        <td className="rb-col-operador">
                                            <span className="rb-validade-badge rb-validade-badge-small">
                                                {rec.operadorNome || rec.operadorId?.nome || '—'}
                                            </span>
                                        </td>
                                        <td className="rb-col-status" style={{ textAlign: 'center' }}>
                                            <span className={`rb-status-badge rb-status-${(rec.status || 'ativo').toLowerCase()}`}>
                                                <Icon name={statusInfo.icon} size={12} />
                                                {statusInfo.label}
                                            </span>
                                        </td>
                                        <td className="rb-col-acoes" style={{ textAlign: 'center' }}>
                                            <div className="rb-actions-cell">
                                                <button className="rb-action-btn rb-action-view" onClick={() => verDetalhes(rec)} title="Ver detalhes">
                                                    <Icon name="visibility" size={16} />
                                                </button>

                                                {!isAnulado && (
                                                    <button className="rb-action-btn rb-action-pdf" onClick={() => exportarPDF(rec)} title="Exportar PDF">
                                                        <Icon name="picture_as_pdf" size={16} />
                                                    </button>
                                                )}

                                                {rec.status === 'Ativo' && (
                                                    <button className="rb-action-btn rb-action-cancel" title="Anular"
                                                        onClick={() => {
                                                            setRecebimentoToAnular(rec);
                                                            setMotivoAnulacao('');
                                                            setShowAnularModal(true);
                                                        }}>
                                                        <Icon name="cancel" size={16} />
                                                    </button>
                                                )}
                                                {rec.status === 'Anulado' && (
                                                    <button className="rb-action-btn rb-action-delete" title="Excluir"
                                                        onClick={() => {
                                                            setRecebimentoToDelete(rec);
                                                            setShowDeleteModal(true);
                                                        }}>
                                                        <Icon name="delete" size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {renderPagination()}

            {/* MODAL CRIAR */}
            {showCreateModal && (
                <div className="rb-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                    <div className="rb-modal-content rb-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-header">
                            <h3>
                                <Icon name="add_box" size={22} />
                                Novo Recebimento
                            </h3>
                            <button className="rb-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
                        </div>
                        <div className="rb-modal-body" ref={bodyModalRef}>
                            <form onSubmit={handleCreate}>

                                {/* TIPO */}
                                <div className="rb-form-section">
                                    <h4>Tipo de Movimento</h4>
                                    <div className="rb-tipo-grid">
                                        {TIPOS_MOVIMENTO.map(t => (
                                            <button
                                                key={t.value}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, tipo: t.value })}
                                                className={`rb-tipo-btn ${formData.tipo === t.value ? 'active' : ''}`}
                                                style={{
                                                    background: formData.tipo === t.value ? `${t.color}10` : '#fff',
                                                    borderColor: formData.tipo === t.value ? t.color : '#e5e7eb',
                                                    color: formData.tipo === t.value ? t.color : '#6b7280'
                                                }}
                                            >
                                                <Icon name={t.icon} size={18} />
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* COMPRA A FORNECEDOR (só se tipo=Sangria) */}
                                {formData.tipo === 'Sangria' && (
                                    <div className="rb-form-section rb-form-section-compra">
                                        <h4>
                                            <Icon name="local_shipping" size={16} />
                                            Pagar Compra a Fornecedor
                                            <span className="rb-form-badge-opcional">(opcional)</span>
                                            {comprasSelecionadas.length > 0 && (
                                                <span className="rb-tag-selected-count">
                                                    {comprasSelecionadas.length} selecionada{comprasSelecionadas.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </h4>

                                        <div className="rb-dropdown" ref={dropdownComprasRef}>
                                            <div
                                                className="rb-dropdown-toggle"
                                                onClick={() => setShowComprasDropdown(!showComprasDropdown)}
                                            >
                                                <Icon name="search" size={16} className="rb-dropdown-icon" />
                                                <span className="rb-dropdown-text">
                                                    {loadingCompras
                                                        ? 'A carregar compras...'
                                                        : comprasDisponiveis.length > 0
                                                            ? (comprasSelecionadas.length > 0
                                                                ? `${comprasSelecionadas.length} compra(s) — ${formatarMoeda(totalComprasSelecionadas)}`
                                                                : 'Escolher compra(s) com NF...')
                                                            : 'Nenhuma compra disponível'}
                                                </span>
                                                <Icon
                                                    name={showComprasDropdown ? 'expand_less' : 'expand_more'}
                                                    size={18}
                                                    className="rb-dropdown-arrow"
                                                />
                                            </div>

                                            {showComprasDropdown && (
                                                <div className="rb-dropdown-panel">
                                                    <div className="rb-dropdown-search">
                                                        <Icon name="search" size={14} />
                                                        <input
                                                            type="text"
                                                            placeholder="Pesquisar nº, fornecedor, NF ou referência..."
                                                            value={searchCompra}
                                                            onChange={(e) => setSearchCompra(e.target.value)}
                                                            autoFocus
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {searchCompra && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setSearchCompra(''); }}
                                                            >
                                                                <Icon name="close" size={14} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="rb-dropdown-list">
                                                        {comprasFiltradas.length === 0 ? (
                                                            <div className="rb-dropdown-empty">
                                                                {searchCompra
                                                                    ? 'Nenhuma compra encontrada'
                                                                    : 'Nenhuma compra com Nota Fiscal disponível'}
                                                            </div>
                                                        ) : (
                                                            comprasPaginadas.map(c => {
                                                                const saldo = extrairValorNumerico(c.saldoDevedorCalculado || c.saldoDevedor);
                                                                const total = extrairValorNumerico(c.total);
                                                                const selecionada = isCompraSelecionada(c);
                                                                const temNF = !!normalizarNF(c.documentoFiscal);
                                                                const semNF = !temNF;
                                                                const podeSelecionar = selecionada || podeSelecionarCompra(c);

                                                                const chaveAtual = getChaveAgrupamentoCompra(c);
                                                                const chavePrimeiro = comprasSelecionadas.length > 0
                                                                    ? getChaveAgrupamentoCompra(comprasSelecionadas[0])
                                                                    : null;
                                                                const nfDiferente = !semNF && !selecionada && chavePrimeiro && chaveAtual !== chavePrimeiro;

                                                                return (
                                                                    <div
                                                                        key={c._id}
                                                                        className={`rb-dropdown-item rb-dropdown-item-compra ${selecionada ? 'selected' : ''} ${!podeSelecionar || semNF ? 'disabled' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (podeSelecionar) {
                                                                                toggleCompraSelecionada(c);
                                                                            } else if (semNF) {
                                                                                showToast('Esta compra não tem Nota Fiscal. Adicione uma NF na compra para poder associar.', 'warning');
                                                                            } else {
                                                                                showToast('Esta compra pertence a uma NF diferente. Limpe a seleção para escolher outra.', 'warning');
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            opacity: !podeSelecionar || semNF ? 0.5 : 1,
                                                                            cursor: !podeSelecionar || semNF ? 'not-allowed' : 'pointer',
                                                                            borderLeft: semNF ? '3px solid #dc2626' : (nfDiferente ? '3px solid #f59e0b' : undefined)
                                                                        }}
                                                                        title={semNF ? 'Compra sem Nota Fiscal — não pode ser associada' : (nfDiferente ? 'NF diferente da selecionada' : '')}
                                                                    >
                                                                        <div className={`rb-dropdown-checkbox ${selecionada ? 'checked' : ''}`}>
                                                                            {selecionada && <Icon name="check_circle" size={14} />}
                                                                            {!selecionada && (semNF || !podeSelecionar) && (
                                                                                <Icon name="block" size={14} style={{ color: semNF ? '#dc2626' : '#f59e0b' }} />
                                                                            )}
                                                                        </div>
                                                                        <div className="rb-dropdown-item-info">
                                                                            <span className="rb-dropdown-item-numero">{c.numero}</span>
                                                                            <span className="rb-dropdown-item-cliente">
                                                                                {c.fornecedorNome || c.fornecedorId?.nome || 'Fornecedor'}
                                                                            </span>
                                                                            <span className="rb-dropdown-item-meta">
                                                                                {c.documentoFiscal ? `NF: ${c.documentoFiscal}` : 'Sem NF'} • {formatarData(c.dataCompra)}
                                                                            </span>
                                                                            <span className="rb-dropdown-item-meta rb-dropdown-item-meta-saldo">
                                                                                Saldo: {formatarMoeda(saldo)}
                                                                            </span>
                                                                            {semNF && (
                                                                                <span className="rb-dropdown-item-meta" style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                                                                    🚫 Sem NF — não pode ser associada
                                                                                </span>
                                                                            )}
                                                                            {nfDiferente && (
                                                                                <span className="rb-dropdown-item-meta" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                                                                                    ⚠️ NF diferente
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className="rb-dropdown-item-valor">
                                                                            {formatarMoeda(total)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>

                                                    {/* 🔥 v20 — Paginação do dropdown de compras */}
                                                    {renderPaginacaoDropdown(paginaCompras, totalPaginasCompras, setPaginaCompras)}

                                                    {comprasSelecionadas.length > 0 && (
                                                        <div className="rb-dropdown-footer">
                                                            <div className="rb-dropdown-footer-total">
                                                                <span>Total:</span>
                                                                <strong>{formatarMoeda(totalComprasSelecionadas)}</strong>
                                                            </div>
                                                            <div className="rb-dropdown-footer-total">
                                                                <span>Saldo a pagar:</span>
                                                                <strong className="rb-dropdown-footer-saldo">
                                                                    {formatarMoeda(totalSaldoComprasSelecionadas)}
                                                                </strong>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="rb-dropdown-clear-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removerTodasCompras();
                                                                }}
                                                            >
                                                                Limpar seleção
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* 🔥 v21 — RECIBOS (SÓ SE TIPO=ENTRADA) */}
                                {formData.tipo === 'Entrada' && (
                                    <div className="rb-form-section">
                                        <h4>
                                            <Icon name="receipt_long" size={16} />
                                            Recibos a Agrupar
                                            <span className="rb-form-badge-opcional">(opcional)</span>
                                            {recibosSelecionados.length > 0 && (
                                                <span className="rb-tag-selected-count">
                                                    {recibosSelecionados.length} selecionado{recibosSelecionados.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </h4>

                                        <div className="rb-dropdown" ref={dropdownRecibosRef}>
                                            <div
                                                className="rb-dropdown-toggle"
                                                onClick={() => setShowRecibosDropdown(!showRecibosDropdown)}
                                            >
                                                <Icon name="search" size={16} className="rb-dropdown-icon" />
                                                <span className="rb-dropdown-text">
                                                    {recibosSelecionados.length > 0
                                                        ? `${recibosSelecionados.length} recibo(s) — ${formatarMoeda(totalSelecionado)}`
                                                        : 'Clique para escolher recibos...'}
                                                </span>
                                                <Icon
                                                    name={showRecibosDropdown ? 'expand_less' : 'expand_more'}
                                                    size={18}
                                                    className="rb-dropdown-arrow"
                                                />
                                            </div>

                                            {showRecibosDropdown && (
                                                <div className="rb-dropdown-panel">
                                                    <div className="rb-dropdown-search">
                                                        <Icon name="search" size={14} />
                                                        <input
                                                            type="text"
                                                            placeholder="Pesquisar por nº, cliente ou fatura..."
                                                            value={searchRecibo}
                                                            onChange={(e) => setSearchRecibo(e.target.value)}
                                                            autoFocus
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {searchRecibo && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSearchRecibo('');
                                                                }}
                                                            >
                                                                <Icon name="close" size={14} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="rb-dropdown-list">
                                                        {loadingRecibos ? (
                                                            <div className="rb-dropdown-empty">A carregar recibos...</div>
                                                        ) : recibosFiltrados.length === 0 ? (
                                                            <div className="rb-dropdown-empty">
                                                                {searchRecibo
                                                                    ? 'Nenhum recibo encontrado'
                                                                    : 'Todos os recibos já estão associados a recebimentos activos'}
                                                            </div>
                                                        ) : (
                                                            recibosPaginados.map(r => {
                                                                const selecionado = isReciboSelecionado(r);
                                                                const valor = extrairValorNumerico(r.valorPago);
                                                                const podeSelecionar = selecionado || podeSelecionarRecibo(r);

                                                                const chaveAtual = getChaveAgrupamento(r);
                                                                const chavePrimeiro = recibosSelecionados.length > 0 ? getChaveAgrupamento(recibosSelecionados[0]) : null;
                                                                const faturaDiferente = !selecionado && chavePrimeiro && chaveAtual !== chavePrimeiro;

                                                                return (
                                                                    <div
                                                                        key={r._id}
                                                                        className={`rb-dropdown-item ${selecionado ? 'selected' : ''} ${!podeSelecionar ? 'disabled' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (podeSelecionar) {
                                                                                toggleReciboSelecionado(r);
                                                                            } else {
                                                                                showToast('Este recibo pertence a uma fatura diferente. Limpe a seleção para escolher outro.', 'warning');
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            opacity: !podeSelecionar ? 0.5 : 1,
                                                                            cursor: !podeSelecionar ? 'not-allowed' : 'pointer',
                                                                            borderLeft: faturaDiferente ? '3px solid #f59e0b' : undefined
                                                                        }}
                                                                        title={faturaDiferente ? 'Fatura diferente da selecionada' : ''}
                                                                    >
                                                                        <div className={`rb-dropdown-checkbox ${selecionado ? 'checked' : ''}`}>
                                                                            {selecionado && <Icon name="check_circle" size={14} />}
                                                                            {!selecionado && !podeSelecionar && <Icon name="block" size={14} style={{ color: '#f59e0b' }} />}
                                                                        </div>
                                                                        <div className="rb-dropdown-item-info">
                                                                            <span className="rb-dropdown-item-numero">{r.numero}</span>
                                                                            <span className="rb-dropdown-item-cliente">
                                                                                {r.clienteInfo?.nome || 'Cliente'}
                                                                            </span>
                                                                            <span className="rb-dropdown-item-meta">
                                                                                Fat: {r.faturaSnapshot?.numero || '—'} • {formatarData(r.dataPagamento)}
                                                                            </span>
                                                                            {r.metodoPagamento && (
                                                                                <span className="rb-dropdown-item-metodo">
                                                                                    {r.metodoPagamento}
                                                                                    {r.referenciaPagamento ? ` • ${r.referenciaPagamento}` : ''}
                                                                                </span>
                                                                            )}
                                                                            {faturaDiferente && (
                                                                                <span className="rb-dropdown-item-meta" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                                                                                    ⚠️ Fatura diferente
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className="rb-dropdown-item-valor">
                                                                            {formatarMoeda(valor)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>

                                                    {/* 🔥 v20 — Paginação do dropdown de recibos */}
                                                    {renderPaginacaoDropdown(paginaRecibos, totalPaginasRecibos, setPaginaRecibos)}

                                                    {recibosSelecionados.length > 0 && (
                                                        <div className="rb-dropdown-footer">
                                                            <div className="rb-dropdown-footer-total">
                                                                <span>Total:</span>
                                                                <strong>{formatarMoeda(totalSelecionado)}</strong>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="rb-dropdown-clear-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setRecibosSelecionados([]);
                                                                    setFormData(prev => ({ ...prev, valor: 0, referencia: '', metodoPagamento: 'Dinheiro' }));
                                                                    setMetodosDetectados([]);
                                                                }}
                                                            >
                                                                Limpar seleção
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* VALOR / DATA */}
                                <div className="rb-form-section">
                                    <h4>Valores e Datas</h4>
                                    <div className="rb-form-row">
                                        <div className="rb-form-group">
                                            <label>Valor Recebido *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                value={formData.valor}
                                                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                                                placeholder="0.00"
                                                required
                                            />
                                            <small className="rb-form-hint">
                                                {recibosSelecionados.length > 0
                                                    ? `Soma automática dos ${recibosSelecionados.length} recibo(s)`
                                                    : comprasSelecionadas.length > 0
                                                        ? `Soma dos totais de ${comprasSelecionadas.length} compra(s)`
                                                        : 'Valor pode ser editado manualmente'}
                                            </small>
                                        </div>
                                        <div className="rb-form-group">
                                            <label>Data *</label>
                                            <input
                                                type="date"
                                                value={formData.data}
                                                onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* MÉTODO / REFERÊNCIA */}
                                <div className="rb-form-section">
                                    <h4>Método e Referência</h4>
                                    <div className="rb-form-row">
                                        <div className="rb-form-group">
                                            <label>Método de Pagamento *</label>
                                            <select
                                                value={formData.metodoPagamento}
                                                onChange={(e) => setFormData({ ...formData, metodoPagamento: e.target.value })}
                                            >
                                                {METODOS_PAGAMENTO.map(m => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                            {metodosDetectados.length > 1 && (
                                                <small className="rb-form-warning">
                                                    ⚠️ {metodosDetectados.length} métodos aplicados — todos identificados na referência
                                                </small>
                                            )}
                                        </div>
                                        <div className="rb-form-group">
                                            <label>
                                                Referência
                                                {formData.metodoPagamento !== 'Dinheiro' && ' *'}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.referencia}
                                                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                                                placeholder={
                                                    formData.metodoPagamento === 'Transferência Bancária' ? 'Ex: TB-2026-001' :
                                                    formData.metodoPagamento === 'Cheque' ? 'Ex: CHQ 001234' :
                                                    formData.metodoPagamento === 'M-Pesa' ? 'Código: 123456' :
                                                    'Detalhes da transação'
                                                }
                                                disabled={formData.metodoPagamento === 'Dinheiro' && metodosDetectados.length === 0 && comprasSelecionadas.length === 0}
                                            />
                                            {comprasSelecionadas.length > 0 && (
                                                <small className="rb-form-hint-italic">
                                                    Referência preenchida automaticamente com base nas compras selecionadas
                                                </small>
                                            )}
                                            {metodosDetectados.length > 1 && (
                                                <small className="rb-form-hint-italic">
                                                    Contém todos os métodos identificados por recibo
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* CONTA DE DESTINO */}
                                <div className="rb-form-section">
                                    <h4>
                                        <Icon name="account_balance" size={16} />
                                        Conta de Destino
                                    </h4>

                                    <div className="rb-tipo-grid">
                                        {TIPOS_CONTA.map(t => (
                                            <button
                                                key={t.value}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, contaTipo: t.value })}
                                                className={`rb-tipo-btn ${formData.contaTipo === t.value ? 'active' : ''}`}
                                            >
                                                <Icon name={t.icon} size={16} />
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>

                                    {formData.contaTipo === 'Caixa' && (
                                        <div className="rb-form-group">
                                            <label>Nome da Caixa *</label>
                                            <input
                                                type="text"
                                                value={formData.contaCaixaNome}
                                                onChange={(e) => setFormData({ ...formData, contaCaixaNome: e.target.value })}
                                                placeholder="Ex: Caixa Principal, Caixa 1..."
                                                list="rb-caixas-list"
                                            />
                                            <datalist id="rb-caixas-list">
                                                {contasDisponiveis.caixas?.map((c, i) => (
                                                    <option key={i} value={c.nome} />
                                                ))}
                                            </datalist>
                                        </div>
                                    )}

                                    {formData.contaTipo === 'Banco' && (
                                        <div className="rb-form-group">
                                            <label>Banco *</label>
                                            {contasDisponiveis.bancos?.length > 0 ? (
                                                <select
                                                    value={formData.contaBancoId}
                                                    onChange={(e) => setFormData({ ...formData, contaBancoId: e.target.value })}
                                                    required
                                                >
                                                    <option value="">Selecione o banco...</option>
                                                    {contasDisponiveis.bancos.map(b => (
                                                        <option key={b._id} value={b._id}>
                                                            {b.label} {b.principal ? '⭐' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p className="rb-form-empty">
                                                    Nenhum banco cadastrado. Vá a Definições → Dados Bancários
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {formData.contaTipo === 'Mobile' && (
                                        <>
                                            <div className="rb-form-group">
                                                <label>Operadora *</label>
                                                <select
                                                    value={formData.contaOperadora}
                                                    onChange={(e) => setFormData({ ...formData, contaOperadora: e.target.value })}
                                                >
                                                    <option value="M-Pesa">M-Pesa</option>
                                                    <option value="e-Mola">e-Mola</option>
                                                    <option value="MBWay">MBWay</option>
                                                </select>
                                            </div>
                                            <div className="rb-form-group">
                                                <label>Número de Telefone</label>
                                                <input
                                                    type="text"
                                                    value={formData.contaNumeroTelefone}
                                                    onChange={(e) => setFormData({ ...formData, contaNumeroTelefone: e.target.value })}
                                                    placeholder="Ex: 84 123 4567"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* OBSERVAÇÕES */}
                                <div className="rb-form-section">
                                    <h4>Observações</h4>
                                    <div className="rb-form-group">
                                        <textarea
                                            value={formData.observacoes}
                                            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                            placeholder="Observações adicionais (opcional)"
                                            rows="2"
                                        />
                                    </div>
                                </div>

                                {/* AÇÕES */}
                                <div className="rb-modal-actions">
                                    <button type="submit" className="rb-modal-btn-primary" disabled={loading}>
                                        {loading ? 'A registar...' : 'Registar Recebimento'}
                                    </button>
                                    <button type="button" className="rb-modal-btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL ESCOLHER MÉTODO */}
            {showMetodoModal && metodosDetectados.length > 0 && (
                <div className="rb-modal-overlay rb-modal-overlay-top" onClick={() => setShowMetodoModal(false)}>
                    <div className="rb-modal-content rb-modal-metodo" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-header">
                            <h3>
                                <Icon name="receipt_long" size={20} />
                                {metodosDetectados.length} Métodos Encontrados
                            </h3>
                            <button className="rb-modal-close" onClick={() => setShowMetodoModal(false)}>×</button>
                        </div>

                        <div className="rb-modal-body">
                            <p className="rb-metodo-info">
                                <Icon name="info" size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />
                                <strong>Todos os métodos serão aplicados</strong> ao recebimento, cada um identificado com o seu recibo.
                                Escolha qual será o <strong>método principal</strong> (opcional):
                            </p>

                            <div className="rb-metodos-lista">
                                {metodosDetectados.map((m, idx) => (
                                    <div
                                        key={m.reciboId}
                                        className="rb-metodo-item"
                                        onClick={() => aplicarMetodoEscolhido(m)}
                                    >
                                        <div className="rb-metodo-item-left">
                                            <div className="rb-metodo-item-badge">
                                                <span className="rb-metodo-item-numero">{idx + 1}</span>
                                            </div>
                                            <div className="rb-metodo-item-info">
                                                <span className="rb-metodo-item-recibo">{m.numero}</span>
                                                <span className="rb-metodo-item-fatura">
                                                    Fatura: {m.faturaNumero}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="rb-metodo-item-center">
                                            <span className="rb-metodo-item-metodo">{m.metodoPagamento}</span>
                                            {m.referencia && (
                                                <span className="rb-metodo-item-referencia">
                                                    Ref: {m.referencia}
                                                </span>
                                            )}
                                        </div>

                                        <div className="rb-metodo-item-right">
                                            <span className="rb-metodo-item-valor">{formatarMoeda(m.valorPago)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="rb-metodo-preview">
                                <div className="rb-metodo-preview-label">
                                    Referência Final
                                </div>
                                <div className="rb-metodo-preview-value">
                                    {metodosDetectados
                                        .map(m => `${m.numero}: ${m.metodoPagamento}${m.referencia ? ` (${m.referencia})` : ''}`)
                                        .join(' | ')}
                                </div>
                            </div>

                            <div className="rb-metodos-actions">
                                <button
                                    className="rb-modal-btn-secondary"
                                    onClick={() => setShowMetodoModal(false)}
                                >
                                    Fechar
                                </button>
                                <button
                                    className="rb-modal-btn-primary"
                                    onClick={aplicarTodasReferencias}
                                >
                                    <Icon name="done_all" size={16} />
                                    Confirmar Todos os Métodos
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL ANULAR */}
            {showAnularModal && recebimentoToAnular && (
                <div className="rb-modal-overlay" onClick={() => setShowAnularModal(false)}>
                    <div className="rb-modal-content rb-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-header">
                            <h3><span className="rb-header-icon">❌</span> Anular Recebimento</h3>
                            <button className="rb-modal-close" onClick={() => setShowAnularModal(false)}>×</button>
                        </div>
                        <div className="rb-modal-body">
                            <div className="rb-confirm-icon rb-confirm-danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Confirmar Anulação</h4>
                            <p>Tem certeza que deseja anular o recebimento <strong>{recebimentoToAnular.numero}</strong>?</p>
                            <p className="rb-confirm-subtext">
                                <Icon name="info" size={14} />
                                O valor será removido do saldo da conta de destino.
                                {recebimentoToAnular.compraFornecedorId && (
                                    <> A compra associada será atualizada (valor pago revertido).</>
                                )}
                            </p>
                            <div className="rb-form-group-cancelamento">
                                <label>Motivo da anulação <span className="rb-required">*</span></label>
                                <textarea
                                    value={motivoAnulacao}
                                    onChange={(e) => setMotivoAnulacao(e.target.value)}
                                    placeholder="Informe o motivo da anulação"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="rb-modal-actions-anular-recebimento">
                            <button className="rb-btn-confirm-danger" onClick={anularRecebimento} disabled={loading || !motivoAnulacao.trim()}>
                                {loading ? 'A processar...' : 'Sim, Anular'}
                            </button>
                            <button className="rb-btn-cancel" onClick={() => setShowAnularModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL EXCLUIR */}
            {showDeleteModal && recebimentoToDelete && (
                <div className="rb-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="rb-modal-content rb-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-header">
                            <h3><span className="rb-header-icon">⚠️</span> Confirmar Exclusão</h3>
                            <button className="rb-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
                        </div>
                        <div className="rb-modal-body">
                            <div className="rb-confirm-icon rb-confirm-danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Remover recebimento?</h4>
                            <p>Tem certeza que deseja remover o recebimento <strong>{recebimentoToDelete.numero}</strong>?</p>
                            <p className="rb-confirm-subtext">
                                <Icon name="info" size={14} /> Esta ação é irreversível.
                            </p>
                        </div>
                        <div className="rb-modal-actions-apagar-recebimento">
                            <button className="rb-btn-confirm-danger" onClick={handleDelete} disabled={loading}>
                                {loading ? 'A processar...' : 'Eliminar'}
                            </button>
                            <button className="rb-btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DETALHES */}
            {showDetailsModal && recebimentoDetalhes && (
                <div className="rb-modal-overlay-detalhes" onClick={() => setShowDetailsModal(false)}>
                    <div className="rb-modal-container-dual" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-content rb-modal-pdf">
                            <div className="rb-modal-header">
                                <h3>
                                    <Icon name={getContaIcon(recebimentoDetalhes.contaDestino?.tipo)} size={22} />
                                    Recebimento {recebimentoDetalhes.numero}
                                </h3>
                                <button className="rb-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                            </div>
                            <div className="rb-modal-body rb-pdf-body">

                                {/* VALOR EM DESTAQUE */}
                                <div className={`rb-valor-destaque ${recebimentoDetalhes.tipo === 'Sangria' ? 'rb-sangria' : 'rb-entrada'}`}>
                                    <div className="rb-valor-destaque-icon">
                                        <Icon
                                            name={getTipoInfo(recebimentoDetalhes.tipo).icon}
                                            size={40}
                                        />
                                    </div>
                                    <div className="rb-valor-destaque-info">
                                        <span className="rb-valor-destaque-label">
                                            {getTipoInfo(recebimentoDetalhes.tipo).label}
                                        </span>
                                        <span className="rb-valor-destaque-valor">
                                            {formatarMoeda(recebimentoDetalhes.valor ?? 0)}
                                        </span>
                                        <span className="rb-valor-destaque-meta">
                                            {formatarData(recebimentoDetalhes.data)} • {recebimentoDetalhes.metodoPagamento || '—'}
                                        </span>
                                    </div>
                                </div>

                                {/* COMPRA A FORNECEDOR ASSOCIADA (singular — legado) */}
                                {recebimentoDetalhes.compraFornecedorId && (
                                    <div className="rb-pdf-resumo-fatura rb-pdf-compra-associada">
                                        <div className="rb-pdf-resumo-titulo">
                                            <Icon name="local_shipping" size={14} /> Compra a Fornecedor Associada
                                        </div>
                                        <div className="rb-compra-associada-info">
                                            <div className="rb-compra-associada-item">
                                                <span className="rb-label">Compra</span>
                                                <span className="rb-value">
                                                    {typeof recebimentoDetalhes.compraFornecedorId === 'object'
                                                        ? (recebimentoDetalhes.compraFornecedorId.numero || '—')
                                                        : '—'}
                                                </span>
                                            </div>
                                            {typeof recebimentoDetalhes.compraFornecedorId === 'object' &&
                                                recebimentoDetalhes.compraFornecedorId.fornecedorNome && (
                                                <div className="rb-compra-associada-item">
                                                    <span className="rb-label">Fornecedor</span>
                                                    <span className="rb-value">
                                                        {recebimentoDetalhes.compraFornecedorId.fornecedorNome}
                                                    </span>
                                                </div>
                                            )}
                                            {typeof recebimentoDetalhes.compraFornecedorId === 'object' &&
                                                recebimentoDetalhes.compraFornecedorId.documentoFiscal && (
                                                <div className="rb-compra-associada-item">
                                                    <span className="rb-label">Nota Fiscal</span>
                                                    <span className="rb-value">
                                                        {recebimentoDetalhes.compraFornecedorId.documentoFiscal}
                                                    </span>
                                                </div>
                                            )}
                                            {typeof recebimentoDetalhes.compraFornecedorId === 'object' && (
                                                <>
                                                    <div className="rb-compra-associada-item">
                                                        <span className="rb-label">Total</span>
                                                        <span className="rb-value">
                                                            {formatarMoeda(recebimentoDetalhes.compraFornecedorId.total ?? 0)}
                                                        </span>
                                                    </div>
                                                    <div className="rb-compra-associada-item">
                                                        <span className="rb-label">Pago</span>
                                                        <span className="rb-value">
                                                            {formatarMoeda(recebimentoDetalhes.compraFornecedorId.valorPago ?? 0)}
                                                        </span>
                                                    </div>
                                                    <div className="rb-compra-associada-item">
                                                        <span className="rb-label">Saldo</span>
                                                        <span className="rb-value text-danger">
                                                            {formatarMoeda(
                                                                recebimentoDetalhes.compraFornecedorId.saldoDevedorCalculado ??
                                                                recebimentoDetalhes.compraFornecedorId.saldoDevedor ??
                                                                Math.max(0,
                                                                    extrairValorNumerico(recebimentoDetalhes.compraFornecedorId.total) -
                                                                    extrairValorNumerico(recebimentoDetalhes.compraFornecedorId.valorPago)
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* 🔥 v19 — COMPRAS AGRUPADAS (sem duplicação, com produtos) */}
                                {recebimentoDetalhes.comprasSnapshot?.length > 0 && (
                                    <div className="rb-pdf-resumo-fatura rb-pdf-compras-agrupadas">
                                        <div className="rb-pdf-resumo-titulo">
                                            <Icon name="local_shipping" size={14} />
                                            Compras a Fornecedor Agrupadas ({recebimentoDetalhes.comprasSnapshot.length})
                                        </div>

                                        <div className="rb-compras-lista">
                                            {recebimentoDetalhes.comprasSnapshot.map((c, idx) => {
                                                const itens = Array.isArray(c.itens) ? c.itens : [];

                                                return (
                                                    <div key={c._id || idx} className="rb-compra-item">
                                                        <div className="rb-compra-item-left">
                                                            <span className="rb-compra-numero">{c.numero || '—'}</span>
                                                            <span className="rb-compra-fornecedor">
                                                                {c.fornecedorNome || 'Fornecedor'}
                                                            </span>
                                                            <span className="rb-compra-meta">
                                                                {c.documentoFiscal ? `NF: ${c.documentoFiscal}` : 'Sem NF'}
                                                                {c.dataCompra ? ` • ${formatarData(c.dataCompra)}` : ''}
                                                            </span>

                                                            {itens.length > 0 && (
                                                                <ul className="rb-compra-produtos">
                                                                    {itens.map((item, i) => (
                                                                        <li key={i} className="rb-compra-produto">
                                                                            <span className="rb-compra-produto-qtd">
                                                                                {formatarQuantidade(item.quantidade)}
                                                                                {item.unidade ? ` ${item.unidade}` : '×'}
                                                                            </span>
                                                                            <span className="rb-compra-produto-nome" title={item.produtoNome}>
                                                                                {item.produtoNome}
                                                                            </span>
                                                                            <span className="rb-compra-produto-subtotal">
                                                                                {formatarMoeda(item.subtotal)}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>

                                                        <div className="rb-compra-item-right">
                                                            <span className="rb-compra-valor">
                                                                {formatarMoeda(c.total)}
                                                            </span>
                                                            {extrairValorNumerico(c.saldoDevedor) > 0 && (
                                                                <span className="rb-compra-saldo">
                                                                    Saldo: {formatarMoeda(c.saldoDevedor)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="rb-compras-resumo">
                                            <div className="rb-compras-resumo-item">
                                                <span>Total:</span>
                                                <strong className="positive">
                                                    {formatarMoeda(
                                                        recebimentoDetalhes.comprasSnapshot.reduce(
                                                            (acc, c) => acc + extrairValorNumerico(c.total), 0
                                                        )
                                                    )}
                                                </strong>
                                            </div>
                                            <div className="rb-compras-resumo-item">
                                                <span>Saldo a pagar:</span>
                                                <strong className="negative">
                                                    {formatarMoeda(
                                                        recebimentoDetalhes.comprasSnapshot.reduce(
                                                            (acc, c) => acc + extrairValorNumerico(c.saldoDevedor), 0
                                                        )
                                                    )}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* DETALHES */}
                                <div className="rb-detalhes-grid">
                                    <div className="rb-fatura-info-item">
                                        <span className="rb-label">Conta de Destino</span>
                                        <span className="rb-value">
                                            <Icon name={getContaIcon(recebimentoDetalhes.contaDestino?.tipo)} size={14} />
                                            {getContaLabel(recebimentoDetalhes.contaDestino)}
                                        </span>
                                    </div>
                                    <div className="rb-fatura-info-item">
                                        <span className="rb-label">Referência</span>
                                        <span className="rb-value">
                                            {recebimentoDetalhes.referencia || '—'}
                                        </span>
                                    </div>
                                    <div className="rb-fatura-info-item">
                                        <span className="rb-label">Operador</span>
                                        <span className="rb-value">
                                            {recebimentoDetalhes.operadorNome || '—'}
                                        </span>
                                    </div>
                                    <div className="rb-fatura-info-item">
                                        <span className="rb-label">Data de Registo</span>
                                        <span className="rb-value">
                                            {formatarData(recebimentoDetalhes.criadoEm)}
                                        </span>
                                    </div>
                                </div>

                                {/* RECIBOS LIGADOS */}
                                {recebimentoDetalhes.origem?.recibosIds?.length > 0 && (
                                    <div className="rb-pdf-resumo-fatura">
                                        <div className="rb-pdf-resumo-titulo">
                                            Recibos Agrupados ({recebimentoDetalhes.origem.recibosIds.length})
                                        </div>

                                        {recebimentoDetalhes.metodosDetalhados?.length > 0 ? (
                                            <div className="rb-recibos-lista">
                                                {recebimentoDetalhes.metodosDetalhados.map((m, idx) => {
                                                    const valorNumerico = extrairValorNumerico(
                                                        m.valor ?? m.valorPago ?? m.valorRecebido
                                                    );

                                                    return (
                                                        <div key={`${m.numeroRecibo || idx}-${idx}`} className="rb-recibo-item">
                                                            <div className="rb-recibo-item-left">
                                                                <span className="rb-recibo-numero">
                                                                    {m.numeroRecibo || '—'}
                                                                </span>
                                                                <span className="rb-recibo-fatura">
                                                                    Fat: {m.faturaNumero || '—'}
                                                                </span>
                                                            </div>

                                                            <div className="rb-recibo-item-right">
                                                                <span className="rb-recibo-metodo">
                                                                    {m.metodoPagamento || '—'}
                                                                </span>
                                                                {m.referencia && (
                                                                    <span className="rb-recibo-referencia">
                                                                        Ref: {m.referencia}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <span className="rb-recibo-valor">
                                                                {formatarMoeda(valorNumerico)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="rb-recibos-lista">
                                                {recebimentoDetalhes.origem.recibosIds.map((r, idx) => (
                                                    <div key={r._id || idx} className="rb-recibo-item rb-recibo-item-simples">
                                                        <span className="rb-recibo-numero">
                                                            {r.numero || '—'}
                                                        </span>
                                                        <span className="rb-recibo-valor">
                                                            {formatarMoeda(r.valorPago ?? 0)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* OBSERVAÇÕES */}
                                {recebimentoDetalhes.observacoes && (
                                    <div className="rb-pdf-observacoes">
                                        <div className="rb-pdf-observacoes-label">Observações</div>
                                        <div className="rb-pdf-observacoes-text">{recebimentoDetalhes.observacoes}</div>
                                    </div>
                                )}

                                {/* MOTIVO ANULAÇÃO */}
                                {recebimentoDetalhes.status === 'Anulado' && recebimentoDetalhes.motivoAnulacao && (
                                    <div className="rb-motivo-anulacao">
                                        <div className="rb-motivo-anulacao-label">
                                            Motivo da Anulação
                                        </div>
                                        <div className="rb-motivo-anulacao-text">
                                            {recebimentoDetalhes.motivoAnulacao}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rb-modal-side-panel">
                            <div className="rb-side-panel-header">
                                <h4>
                                    <Icon name="settings" size={18} />
                                    Ações
                                </h4>
                            </div>
                            <div className="rb-side-panel-body">
                                <div className="rb-side-panel-status">
                                    <span className="rb-status-label">Status</span>
                                    <span className={`rb-status-badge rb-status-${(recebimentoDetalhes.status || 'ativo').toLowerCase()}`}>
                                        <Icon name={getStatusInfo(recebimentoDetalhes.status).icon} size={12} />
                                        {getStatusInfo(recebimentoDetalhes.status).label}
                                    </span>
                                </div>

                                {recebimentoDetalhes.status === 'Ativo' && (
                                    <>
                                        <div className="rb-side-panel-divider"></div>
                                        <button
                                            className="rb-side-panel-btn rb-side-panel-btn-primary"
                                            onClick={() => exportarPDF(recebimentoDetalhes)}
                                            title="Exportar PDF"
                                        >
                                            <Icon name="picture_as_pdf" size={18} />
                                            <span>Exportar PDF</span>
                                        </button>

                                        <div className="rb-side-panel-divider"></div>
                                        <button
                                            className="rb-side-panel-btn rb-side-panel-btn-danger"
                                            onClick={() => {
                                                setRecebimentoToAnular(recebimentoDetalhes);
                                                setShowDetailsModal(false);
                                                setShowAnularModal(true);
                                            }}
                                        >
                                            <Icon name="cancel" size={18} />
                                            <span>Anular Recebimento</span>
                                        </button>
                                    </>
                                )}

                                {recebimentoDetalhes.status === 'Anulado' && (
                                    <>
                                        <div className="rb-side-panel-divider"></div>
                                        <button
                                            className="rb-side-panel-btn rb-side-panel-btn-danger"
                                            onClick={() => {
                                                setRecebimentoToDelete(recebimentoDetalhes);
                                                setShowDetailsModal(false);
                                                setShowDeleteModal(true);
                                            }}
                                        >
                                            <Icon name="delete" size={18} />
                                            <span>Excluir Definitivamente</span>
                                        </button>
                                    </>
                                )}

                                <div className="rb-side-panel-divider"></div>

                                <button
                                    className="rb-side-panel-btn rb-side-panel-btn-ghost"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    <Icon name="close" size={18} />
                                    <span>Fechar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} type={toast.type} onClose={closeToast} />
        </div>
    );
}